import React from "react";

import { FormattedMessage, useIntl } from "react-intl";

import {
  alpha,
  Box,
  Button,
  darken,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/styles";

const FilterStyledRadio = styled(Radio, {
  shouldForwardProp: (prop) => prop !== "customcolor",
})(({ customcolor }) => ({
  color: `${customcolor} !important`,
  "&.Mui-checked": {
    color: `${customcolor} !important`,
  },
  "& .MuiSvgIcon-root": {
    fill: `${customcolor} !important`,
  },
  "&.Mui-checked .MuiSvgIcon-root": {
    fill: `${darken(customcolor, 0.2)} !important`,
  },
}));

const CustomButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "customcolor",
})(({ customcolor }) => ({
  color: `${customcolor} !important`,
  borderColor: `${customcolor} !important`,

  "&:hover": {
    color: `${darken(customcolor, 0.1)} !important`,
    borderColor: `${darken(customcolor, 0.1)} !important`,
    backgroundColor: `${alpha(customcolor, 0.1)} !important`,
  }
}));

export default function ArticleFeedback({
  state,
  handleInitialFeedback,
  resetFeedbackState,
  handleChange,
  handleFeedbackSubmit
}) {
  const intl = useIntl();

  const { clientportal, loadingFeedback } = state;

  const defaultClientColor = clientportal?.defaultcolor;

  function skeletonFeedBack() {
    return (
      <Box className="feedback-container"
        sx={{
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid #e0e0e0'
        }}
      >
        <Skeleton variant="text" width="25%" height={24} />

        <Box sx={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <Skeleton variant="rectangular" width="64px" height={36} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="64px" height={36} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    )
  }

  if (loadingFeedback) {
    return skeletonFeedBack();
  }

  return (
    <Box className="feedback-container"
      sx={{
        marginTop: '40px',
        paddingTop: '20px',
        borderTop: '1px solid #e0e0e0'
      }}
    >
      {state.feedbackState === 'initial' && (
        <Box id="initial-feedback-buttons">
          <Typography variant="h6">
            <FormattedMessage id="article.feedback.wasithelpful" />
          </Typography>

          <CustomButton
            variant="outlined"
            onClick={() => handleInitialFeedback(true)}
            customcolor={defaultClientColor}
            sx={{
              marginRight: '10px', marginTop: '10px',
            }}
          >
            <FormattedMessage id="yes" />
          </CustomButton>

          <CustomButton
            variant="outlined"
            onClick={() => handleInitialFeedback(false)}
            customcolor={defaultClientColor}
            sx={{
              marginTop: '10px'
            }}
          >
            <FormattedMessage id="no" />
          </CustomButton>
        </Box>
      )}

      {state.feedbackState === 'detailed' && (
        <Box id="feedback-details">
          <Typography variant="h6">
            <FormattedMessage id="article.feedback.thankyouevaluation" />
          </Typography>

          <FormControl component="fieldset" sx={{ marginTop: '20px', width: '100%' }}>
            <FormLabel
              component="legend"
              sx={{
                fontSize: '1.6rem',
                fontWeight: 'bolder',
                '&.Mui-focused': { color: 'rgba(0, 0, 0, 1)' },
                marginBottom: '10px'
              }}
            >
              <FormattedMessage id="article.feedback.title" />
            </FormLabel>

            <RadioGroup
              name="feedbackReason"
              value={state.feedbackReason}
              onChange={handleChange}
              sx={{ marginTop: '10px' }}
            >
              {state.feedbackOptions.map(option => (
                <React.Fragment key={option.id}>
                  <FormControlLabel
                    value={option.id.toString()}
                    control={
                      <FilterStyledRadio customcolor={defaultClientColor} />
                    }
                    label={<span dangerouslySetInnerHTML={{ __html: option.description }} />}
                    labelPlacement="end"
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "6px",
                      ".MuiFormControlLabel-label": { margin: 0, lineHeight: 1 },
                    }}
                  />

                  {option.id === -1 && state.feedbackReason === '-1' && (
                    <Box sx={{ paddingLeft: '32px', paddingRight: '10px', marginBottom: '10px' }}>
                      <TextField
                        name="descriptionText"
                        label={intl.formatMessage({ id: "description" })}
                        placeholder={intl.formatMessage({ id: "article.feedback.detaildescription" })}
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        value={state.descriptionText}
                        onChange={handleChange}
                        sx={{ backgroundColor: '#fff' }}
                      />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </RadioGroup>

            <Box sx={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <CustomButton
                variant="outlined"
                customcolor={defaultClientColor}
                onClick={resetFeedbackState}
              >
                <FormattedMessage id="cancel" />
              </CustomButton>

              <CustomButton
                variant="outlined"
                customcolor={defaultClientColor}
                onClick={handleFeedbackSubmit}
              >
                <FormattedMessage id="article.feedback.send" />
              </CustomButton>
            </Box>
          </FormControl>
        </Box>
      )}

      {state.feedbackState === 'thankyou' && (
        <Typography variant="h6" id="feedback-thank-you">
          <FormattedMessage id="article.feedback.sent" />
        </Typography>
      )}

      {state.feedbackState === 'alreadyVoted' && (
        <Typography variant="h6" id="feedback-already-voted" sx={{ color: '#666', fontWeight: 'bold' }}>
          <FormattedMessage id="article.feedback.alreadysent" />
        </Typography>
      )}
    </Box>
  )
}